// Single Vuln Row
// ----------------
// Tailwind-styled table row for each vulnerability.
//
// The Status column was dropped in v1: every vuln reaching this surface
// is by definition Open (observed in the latest completed test), so the
// column was a constant value and pure noise. The underlying
// vuln.vulnStatus field is still present on the model for legacy
// readback paths; it's just not surfaced in this row.

import VulnerabilityClass from "@/models/vulnerability";
import { formatDateWithLocale } from "@/utils/date";
import {
  getSeverityDotStyle,
  getSeverityLabel,
} from "@/utils/severityStyles";
import React, { useCallback } from "react";

interface SingleVulnRowProps {
  vuln: VulnerabilityClass;
  position: number;
  onOpenVuln: (position: number) => void;
}

function SingleVulnRow({
  vuln,
  position,
  onOpenVuln,
}: SingleVulnRowProps) {
  const handleOpenVuln = useCallback(() => {
    onOpenVuln(position);
  }, [onOpenVuln, position]);

  const riskStyle = getSeverityDotStyle(vuln.riskRating);
  const riskLabel = getSeverityLabel(vuln.riskRating);
  const vulnId = vuln.reference || vuln.id.slice(0, 8);

  return (
    // Cell order matches the header in VulnerabilityTable.tsx and the
    // Dashboard's Open Vulnerabilities table: Risk, Reference,
    // Vulnerability, First Found, Last Seen.
    <tr
      className="border-b border-gray-100 transition-colors hover:bg-gray-50/50 cursor-pointer"
      onClick={handleOpenVuln}
    >
      <td className="px-4 py-3 whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <span className={`h-2 w-2 rounded-full ${riskStyle.dot} shrink-0`} />
          <span className={`text-sm font-medium ${riskStyle.label}`}>{riskLabel}</span>
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
        {vulnId}
      </td>
      <td className="px-4 py-3">
        <span className="text-sm font-medium text-gray-900 line-clamp-2 break-words">
          {vuln.title || vuln.vulnName}
        </span>
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
        {vuln.dateFirstFound ? formatDateWithLocale(vuln.dateFirstFound) : "—"}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
        {vuln.dateLastSeen ? formatDateWithLocale(vuln.dateLastSeen) : "—"}
      </td>
    </tr>
  );
}

export default SingleVulnRow;
